/**
 * FmRiskWorkspace — governed risk/alert workspace SHELL.
 * A reusable design-system archetype so Sentry and any
 * future risk/alert page never assembles maps, ranked lists, filters, evidence
 * panes, and metric rails ad hoc in route code. The shell encodes the layout +
 * semantic props centrally; route pages pass DATA, CALLBACKS, and SEMANTIC MODES
 * only.
 *
 *  (1) STRUCTURE —
 *      map + callout rail: dominant map region on the left with a source/
 *          provenance + legend relationship.
 *      ranked table + narrative rail: the ranked alert list on the right
 *          with interpretation living beside it (the detail/evidence pane).
 *      tabbed/temporal insight module: the filter rail (governed segmented/
 *          select/chip controls) + the metric rail below the status band.
 *      map story + timeline: the full-width timeline strip below
 *          the map/list, reduced-motion safe (delegated to FmMapTimeline).
 *  (2) FM COMPONENT OWNING LAYOUT — FmRiskWorkspace owns the status-band +
 *      metric-rail rhythm, filter-rail wrap + suppression counts,
 *      banner-above-body placement, the map beside the list+detail column on
 *      FmSplit's media-dominant recipe, and the full-width timeline strip. It COMPOSES
 *      (never clones): FmSectionBand, FmKeyFigures, FmChip, FmSelector/FmSelect/
 *      FmChip, FmBanner, the shared map element + FmMapLegend, FmSplit,
 *      FmListRow.Group/FmListRow + FmRowActions, FmMapTimeline, FmText/FmHeading.
 *  (3) SEMANTIC PROPS THE PAGE PASSES — title/eyebrow, status(severity+label),
 *      freshness as a separate operational label, shared evidence/provenance,
 *      metrics, filter descriptors, suppression counts, banner
 *      descriptors, a governed map figure (assetKey OR live slot), ranked alert
 *      rows, selectedId + onSelectRow, a selected-item detail descriptor,
 *      timeline steps + onStepChange, and empty/error/loading labels. NO
 *      className/style/raw dimensions/raw colours/map-or-chart JSX injection.
 *  (4) SEVERITY — no all-clear "green banner" reassurance. Severity language is
 *      SEMANTIC ONLY (none/context, advisory/watch, warning, critical); the DS
 *      resolves each to a token, so a raw colour can never cross the boundary.
 *
 * NO-FALLBACK: unpriced/missing values are the caller's responsibility to render
 * as an em-dash; the shell fabricates no alert/metric values. Empty/error/loading
 * are explicit, labelled states (never a silent 0 or a fake "all clear").
 */

import React, { useMemo } from 'react';
import { FmSectionBand } from './FmReportComposition';
import { FmBanner } from './FmBanner';
import { FmMapTimeline } from './FmMapTimeline';
import { FmHeading } from './FmComposition';
import { FmEvidence } from './FmEvidence';
import { FmSplit } from './FmLayout';
import { FmMapElementView } from './elements/FmMediaElements';
import type { FmMapElement } from './elements/elements.types';
import { AlertList, DetailPane, FilterRegion, StatusRegion } from './FmRiskWorkspace.regions';
import { BANNER_TONE } from './FmRiskWorkspace.severity';
import type {
  FmRiskAlertRow,
  FmRiskBanner,
  FmRiskDetail,
  FmRiskTimeline,
  FmRiskWorkspaceLabels,
  FmRiskWorkspaceMap,
  FmRiskWorkspaceProps,
} from './FmRiskWorkspace.types';
import './FmRiskWorkspace.css';

export type {
  FmRiskWorkspaceProps,
  FmRiskWorkspaceTone,
  FmRiskWorkspaceMap,
  FmRiskWorkspaceLabels,
  FmRiskSeverity,
  FmRiskStatus,
  FmRiskLabel,
  FmRiskMetric,
  FmRiskIconId,
  FmRiskFilter,
  FmRiskFilterTier,
  FmRiskFilterChip,
  FmRiskSegmentOption,
  FmRiskSelectOption,
  FmRiskSuppression,
  FmRiskBanner,
  FmRiskAlertRow,
  FmRiskAction,
  FmRiskActionConfirm,
  FmRiskDetail,
  FmRiskDetailFact,
  FmRiskTimeline,
} from './FmRiskWorkspace.types';

/* ── Banner stack (coverage-gap / stale-source; never an all-clear) ─────── */

const BannerStack: React.FC<{ banners: FmRiskBanner[]; testId: string }> = ({ banners, testId }) => (
  <div className="fm-risk-workspace__banners" data-testid={`${testId}-banners`}>
    {banners.map((banner) => (
      <FmBanner
        key={banner.id}
        tone={BANNER_TONE[banner.tone]}
        title={banner.title}
        description={banner.description}
        data-testid={`${testId}-banner-${banner.id}`}
      />
    ))}
  </div>
);

type FmRiskMapFigure = FmMapElement['figure'];

/* ── Map region (the shared map element; no route-owned map JSX) ─────────── */

const MapRegion: React.FC<{ map?: FmRiskWorkspaceMap; figure?: FmRiskMapFigure; testId: string }> = ({
  map,
  figure,
  testId,
}) => (
  <div className="fm-risk-workspace__map" data-testid={`${testId}-map`}>
    <FmMapElementView
      element={{
        element: 'map',
        layoutPreset: 'timeline-focus',
        figure,
        legend: map?.legend,
        source: map?.source,
        interactionHint: map?.interactionHint,
        emptyLabel: map?.emptyLabel,
        errorLabel: map?.errorLabel,
        loading: map?.loading,
        reveal: map?.reveal ?? true,
        testId: `${testId}-map-media`,
      }}
    />
  </div>
);

/* ── Full-width timeline strip ──────────────────────────────────────────── */

const TimelineStrip: React.FC<{ timeline: FmRiskTimeline; title?: string; testId: string }> = ({
  timeline,
  title,
  testId,
}) => (
  <section className="fm-risk-workspace__timeline" data-testid={`${testId}-timeline`} aria-label={title}>
    {title ? (
      <FmHeading level={3} visualLevel={4} className="fm-risk-workspace__region-title">
        {title}
      </FmHeading>
    ) : null}
    <FmMapTimeline
      steps={timeline.steps}
      current={timeline.current}
      start={timeline.start}
      end={timeline.end}
      mode={timeline.mode}
      modeLabel={timeline.modeLabel}
      scrubberLabel={timeline.scrubberLabel}
      qualifiers={timeline.qualifiers}
      onStepChange={timeline.onStepChange}
      autoPlay={timeline.autoPlay}
      onAutoPlayToggle={timeline.onAutoPlayToggle}
      playLabel={timeline.playLabel}
      pauseLabel={timeline.pauseLabel}
      data-testid={`${testId}-timeline-control`}
    />
  </section>
);

function rowMapFeatureId(row: FmRiskAlertRow): string {
  return row.mapFeatureId ?? row.id;
}

/* ── Work surface body (loading state OR the map/list/detail grid) ──────── */

interface WorkspaceBodyProps {
  loading: boolean;
  loadingLabel?: string;
  map?: FmRiskWorkspaceMap;
  mapFigure?: FmRiskMapFigure;
  listTitle?: string;
  rows?: FmRiskAlertRow[];
  selectedId?: string;
  onSelectRow?: (id: string) => void;
  listEmptyLabel?: string;
  detail?: FmRiskDetail;
  detailTitle?: string;
  detailEmptyLabel?: string;
  labels: FmRiskWorkspaceLabels;
  testId: string;
}

const WorkspaceBody: React.FC<WorkspaceBodyProps> = ({
  loading,
  loadingLabel,
  map,
  mapFigure,
  listTitle,
  rows,
  selectedId,
  onSelectRow,
  listEmptyLabel,
  detail,
  detailTitle,
  detailEmptyLabel,
  labels,
  testId,
}) => {
  if (loading) {
    // NO-FALLBACK: aria-busy conveys the loading state; copy is rendered only
    // when the caller supplies a localized label (no em-dash placeholder).
    return (
      <div
        className="fm-risk-workspace__empty"
        data-state="loading"
        aria-busy="true"
        data-testid={`${testId}-loading`}
      >
        {loadingLabel}
      </div>
    );
  }
  return (
    <FmSplit
      side="start"
      balance="media-dominant"
      // Stacked, the ranked alerts and the selected alert's evidence are the
      // owner's action surface; the map follows as spatial context.
      stackOrder="content-first"
      contentSurface="plain"
      className="fm-risk-workspace__body"
      data-testid={`${testId}-body`}
      graphic={<MapRegion map={map} figure={mapFigure} testId={testId} />}
      content={
        /* List + detail share ONE column so the two cards stack beside the map. */
        <div className="fm-risk-workspace__aside">
          <AlertList
            listTitle={listTitle}
            rows={rows}
            selectedId={selectedId}
            onSelectRow={onSelectRow}
            emptyLabel={listEmptyLabel}
            actionsLabel={labels.rowActions}
            overflowLabel={labels.rowActionsOverflow}
            testId={`${testId}-list`}
          />
          <DetailPane
            detail={detail}
            title={detailTitle}
            emptyLabel={detailEmptyLabel}
            actionsLabel={labels.detailActions}
            overflowLabel={labels.detailActionsOverflow}
            testId={`${testId}-detail`}
          />
        </div>
      }
    />
  );
};

/* ── FmRiskWorkspace ────────────────────────────────────────────────────── */

export const FmRiskWorkspace: React.FC<FmRiskWorkspaceProps> = ({
  title,
  eyebrow,
  tone = 'deep',
  status,
  freshness,
  evidence,
  metrics,
  filters,
  filterLabel,
  filtersMoreLabel,
  suppression,
  banners,
  map,
  listTitle,
  rows,
  selectedId,
  onSelectRow,
  listEmptyLabel,
  detail,
  detailTitle,
  detailEmptyLabel,
  labels,
  timeline,
  timelineTitle,
  loading = false,
  errorLabel,
  loadingLabel,
  'data-testid': dataTestId = 'fm-risk-workspace',
}) => {
  // Record ids and map feature ids are deliberately distinct. The shell owns
  // both-direction translation and publishes only currently visible features.
  const mapFigure = useMemo<FmRiskMapFigure | undefined>(() => {
    const figure = map?.figure;
    if (!figure) return undefined;
    if (figure.kind === 'live') {
      const visibleRows = rows ?? [];
      const selectedRow = visibleRows.find((row) => row.id === selectedId);
      const originalFeatureSelect = figure.slotProps?.onFeatureSelect;
      return {
        ...figure,
        slotProps: {
          ...figure.slotProps,
          selectedFeatureId: selectedRow ? rowMapFeatureId(selectedRow) : undefined,
          visibleFeatureIds: [...new Set(visibleRows.map(rowMapFeatureId))],
          onFeatureSelect: (featureId) => {
            originalFeatureSelect?.(featureId);
            const row = visibleRows.find((candidate) => rowMapFeatureId(candidate) === featureId);
            if (row) onSelectRow?.(row.id);
          },
        },
      };
    }
    return figure;
  }, [map?.figure, onSelectRow, rows, selectedId]);

  return (
    <FmSectionBand tone={tone} width="full" density="compact" data-testid={`${dataTestId}-band`}>
      <div className="fm-risk-workspace" data-loading={loading || undefined} data-testid={dataTestId}>
        <StatusRegion
          title={title}
          eyebrow={eyebrow}
          status={status}
          freshness={freshness}
          metrics={metrics}
          testId={`${dataTestId}-status`}
        />

        <FilterRegion
          filters={filters}
          filterLabel={filterLabel}
          moreLabel={filtersMoreLabel}
          suppression={suppression}
          testId={`${dataTestId}-filters`}
        />

        {errorLabel ? <FmBanner tone="error" title={errorLabel} data-testid={`${dataTestId}-error`} /> : null}

        {banners && banners.length > 0 ? <BannerStack banners={banners} testId={dataTestId} /> : null}

        <WorkspaceBody
          loading={loading}
          loadingLabel={loadingLabel}
          map={map}
          mapFigure={mapFigure}
          listTitle={listTitle}
          rows={rows}
          selectedId={selectedId}
          onSelectRow={onSelectRow}
          listEmptyLabel={listEmptyLabel}
          detail={detail}
          detailTitle={detailTitle}
          detailEmptyLabel={detailEmptyLabel}
          labels={labels}
          testId={dataTestId}
        />

        {evidence ? <FmEvidence evidence={evidence} data-testid={`${dataTestId}-evidence`} /> : null}

        {timeline ? <TimelineStrip timeline={timeline} title={timelineTitle} testId={dataTestId} /> : null}
      </div>
    </FmSectionBand>
  );
};

FmRiskWorkspace.displayName = 'FmRiskWorkspace';

export default FmRiskWorkspace;
